import { useState, useEffect } from "react";
import SettingsService from "../services/SettingsService";
import apiClient from "../auth/apiClient";

const settingsService = new SettingsService(apiClient);

/**
 * Hook to retrieve the shared facility IDs for a given facility.
 * Returns an array of [facilityID, ...sharedFacilityIDs] if the
 * setting.sharedfacilities setting is configured, otherwise an empty array.
 *
 * @param {string} facilityID - The facility ID to look up shared facilities for
 * @param {object} [options] - Optional configuration
 * @param {function} [options.onError] - Callback invoked on non-404 errors, receives the error object
 * @returns {string[]} Array of facility IDs including the source facility
 */
const useSharedFacilities = (facilityID, { onError } = {}) => {
  const [sharedFacilityIDs, setSharedFacilityIDs] = useState([]);

  useEffect(() => {
    if (!facilityID) return;

    const fetchSharedFacilities = async () => {
      try {
        const res = await settingsService.getSettings(facilityID);
        const raw = res?.data?.["setting.sharedfacilities"];
        if (raw && raw !== '[]') {
          const cleaned = raw.replace(/[[\]]/g, '').split(',').map(s => s.trim()).filter(Boolean);
          if (cleaned.length > 0) {
            setSharedFacilityIDs([facilityID, ...cleaned]);
          }
        }
      } catch (err) {
        if (err?.response == null || err?.response?.status !== 404) {
          if (onError) onError(err);
        }
      }
    };

    fetchSharedFacilities();
  }, [facilityID]);

  return sharedFacilityIDs;
};

export default useSharedFacilities;
